import React, { useState } from 'react';
import { unifiedApiService } from '../../services/unified';

interface CompanyContext {
  id?: string;
  name?: string;
  industry?: string;
  description?: string;
  benefits?: string[];
  headquarters?: {
    city?: string;
    state?: string;
    country?: string;
  };
}

interface GeneratedJobContent {
  description?: string;
  requirements?: string[];
  benefits?: string[];
  skills?: string[];
}

interface AIGenerateAllButtonProps {
  jobTitle: string;
  experienceLevel?: string;
  location?: string;
  companyContext?: CompanyContext;
  onGenerated: (content: GeneratedJobContent) => void;
  disabled?: boolean;
}

export const AIGenerateAllButton: React.FC<AIGenerateAllButtonProps> = ({
  jobTitle,
  experienceLevel,
  location,
  companyContext,
  onGenerated,
  disabled = false,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleGenerateAll = async () => {
    if (!jobTitle.trim()) {
      setError('Please provide a job title first');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setProgress('Generating all content with AI...');

    try {
      // Use single API call for better performance
      const response = await unifiedApiService.ai.generateAllJobContent({
        job_title: jobTitle,
        experience_level: experienceLevel,
        company_id: companyContext?.id,
        company_name: companyContext?.name,
        company_industry: companyContext?.industry,
        company_description: companyContext?.description,
        location,
      });

      const result: GeneratedJobContent = {
        description: response.description,
        requirements: response.requirements,
        benefits: response.benefits,
        skills: response.skills,
      };

      if (response.errors && response.errors.length > 0) {
        console.warn('Some AI generations had errors:', response.errors);
      }

      setProgress('');
      onGenerated(result);
    } catch (err: any) {
      setError(err.message || 'Failed to generate job content');
    } finally {
      setIsGenerating(false);
      setProgress('');
    }
  };

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={handleGenerateAll}
        disabled={disabled || isGenerating || !jobTitle}
        className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
      >
        {isGenerating ? (
          <>
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>{progress || 'Generating...'}</span>
          </>
        ) : (
          <>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <span>✨ AI Generate All Fields</span>
          </>
        )}
      </button>
      {error && <span className="text-sm text-red-500">{error}</span>}
    </div>
  );
};

export default AIGenerateAllButton;
